import { gql } from "@apollo/client";

// Queries for the Explore (Discover redesign) pages. Additive — the legacy social-discover
// queries in ./discover.ts are left untouched. Backed by graphql/schemas/explore.ts.
// `monthData` and `details` are JSON-scalar fields (selected without a subselection).

export const EXPLORE_OVERVIEW = gql`
	query ExploreOverview($language: String) {
		exploreOverview(language: $language) {
			countryCount
			editorsPicks {
				_id
				slug
				name
				flag
				tagline
				heroImage
				label
				lat
				lng
				destinationCount
			}
			vibes {
				vibe
				count
			}
			topIslands {
				_id
				slug
				name
				country
				countrySlug
				type
				label
				tagline
				lat
				lng
			}
		}
	}
`;

export const EXPLORE_COUNTRIES = gql`
	query ExploreCountries($vibe: String, $travelerType: String, $region: String, $month: String, $search: String, $withGuide: Boolean, $limit: Int, $offset: Int, $language: String) {
		exploreCountries(
			vibe: $vibe
			travelerType: $travelerType
			region: $region
			month: $month
			search: $search
			withGuide: $withGuide
			limit: $limit
			offset: $offset
			language: $language
		) {
			_id
			slug
			name
			region
			flag
			tagline
			heroImage
			label
			vibe
			# What the country's DESTINATIONS offer, which is not the same as its own two-entry
			# vibe — see the resolver. The "Type of trip" filter matches against both.
			destinationVibes
			bestMonths
			lat
			lng
			destinationCount
		}
	}
`;

export const EXPLORE_COUNTRY = gql`
	query ExploreCountry($slug: String!, $language: String) {
		exploreCountry(slug: $slug, language: $language) {
			country {
				_id
				slug
				name
				region
				flag
				tagline
				description
				heroImage
				highlights
				vibe
				travelerType
				bestMonths
				bestTimeToVisit
				lat
				lng
				monthData
				details
			}
			sections {
				type
				title
				description
				count
			}
			destinations {
				_id
				slug
				name
				countrySlug
				type
				label
				tagline
				vibe
				bestMonths
				lat
				lng
			}
		}
	}
`;

export const EXPLORE_DESTINATION = gql`
	query ExploreDestination($countrySlug: String!, $slug: String!, $language: String) {
		exploreDestination(countrySlug: $countrySlug, slug: $slug, language: $language) {
			_id
			slug
			name
			country
			countrySlug
			type
			label
			tagline
			description
			bestKnownFor
			vibe
			travelerType
			budgetLevel
			avgStayDays
			stayDayTrip
			bestMonths
			bestTimeToVisit
			monthData
			whereToStay
			localTip
			funFacts
			neighborhoods {
				name
				description
				bestFor
				tags
			}
			dayTrips
			bookingUrl
			toursUrl
			lat
			lng
			locationiqId
			details
		}
	}
`;

// Slim, unpaginated marker feed for the explore map symbol layers (~4.2k rows).
// Deliberately selects no _id so Apollo keeps the rows as one cached blob under the
// query root instead of normalizing thousands of entities.
export const EXPLORE_MARKERS = gql`
	query ExploreMarkers {
		exploreMarkers {
			slug
			name
			countrySlug
			type
			label
			lat
			lng
		}
	}
`;

export const EXPLORE_DESTINATIONS = gql`
	query ExploreDestinations(
		$countrySlug: String
		$type: String
		$vibe: String
		$travelerType: String
		$label: String
		$search: String
		$withGuide: Boolean
		$limit: Int
		$offset: Int
		$language: String
	) {
		exploreDestinations(
			countrySlug: $countrySlug
			type: $type
			vibe: $vibe
			travelerType: $travelerType
			label: $label
			search: $search
			withGuide: $withGuide
			limit: $limit
			offset: $offset
			language: $language
		) {
			_id
			slug
			name
			country
			countrySlug
			type
			label
			tagline
			heroImage
			vibe
			bestMonths
			avgStayDays
			lat
			lng
		}
	}
`;

/**
 * The whole guide document for one geo, as the country and destination pages render it.
 *
 * `geo` is "thailand" for a country and "thailand/bangkok" for a destination — the same
 * canonical key the knowledge base uses, and the page's own URL path.
 *
 * Returns a JSON blob on purpose. The shape is the CountryData/CityData contract these
 * pages already declare, it is generated rather than hand-modelled, and enumerating ~25
 * nested fields in SDL would mean editing the schema every time a section gains a field.
 *
 * Always returns something for a published geo: a rich generated guide where the
 * knowledge base covers it, and a thinner one synthesised from the Explore document
 * otherwise. Null only when the geo does not exist.
 */
export const EXPLORE_GUIDE = gql`
	query ExploreGuide($geo: String!, $language: String) {
		exploreGuide(geo: $geo, language: $language)
	}
`;

/**
 * The blog index's list — spotlight, categories and articles, in the shape `Blog.tsx` already
 * consumes. Backed by `graphql/schemas/blog.ts`, not `explore.ts`: that module already imports
 * `buildExploreGuide` from `explore.ts` for the SSR discover loader, so importing its
 * `loadBlogList` back into `explore.ts` would close an import cycle. Same JSON-blob rationale
 * as `EXPLORE_GUIDE` above — it is the SSR loader's own output, unmodelled in SDL.
 */
export const BLOG_LIST = gql`
	query BlogList($language: String) {
		blogList(language: $language)
	}
`;
